import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Copy, 
  Check, 
  Code, 
  ExternalLink, 
  RotateCcw,
  Eye,
  Settings,
  Users
} from 'lucide-react';
import { Dealer, EmbedCode as EmbedCodeType } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DealerInfo = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .dealer-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 8px;
  }
  
  .dealer-details {
    color: #6b7280;
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
  }
`;

const TokenInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  .token-header {
    display: flex;
    align-items: center;
    justify-content: between;
    margin-bottom: 8px;
    
    h3 {
      margin: 0;
      color: #0c4a6e;
      font-size: 1rem;
      font-weight: 600;
    }
  }
  
  .token-value {
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.875rem;
    color: #374151;
    background: white;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    word-break: break-all;
  }
  
  .token-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
  }
`;

const CodeSection = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-bottom: 16px;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const CodeBlock = styled.div`
  background: #1f2937;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const CodeHeader = styled.div`
  background: #374151;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: between;
  
  .code-title {
    color: #f9fafb;
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

const CodeContent = styled.pre`
  margin: 0;
  padding: 20px;
  color: #e5e7eb;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const CopyButton = styled.button<{ copied?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.copied ? '#10b981' : '#6b7280'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${props => props.copied ? '#059669' : '#4b5563'};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: ${props => 
    props.variant === 'primary' ? 'none' : 
    props.variant === 'danger' ? '1px solid #f87171' : '1px solid #d1d5db'
  };
  background: ${props => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 
    props.variant === 'danger' ? '#fef2f2' : 'white'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : 
    props.variant === 'danger' ? '#dc2626' : '#374151'
  };
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => {
      if (props.variant === 'primary') {
        return 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);';
      } else if (props.variant === 'danger') {
        return 'background: #fee2e2; border-color: #fca5a5;';
      } else {
        return 'background: #f3f4f6; border-color: #9ca3af;';
      }
    }}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 24px;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: 12px 16px;
  border: none;
  background: none;
  color: ${props => props.active ? '#ed2024' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#ed2024' : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    color: #ed2024;
  }
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.6;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

type EmbedTab = 'script' | 'widget' | 'floating' | 'full';

const EmbedCode: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [embedCode, setEmbedCode] = useState<EmbedCodeType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<EmbedTab>('full');
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (id) {
      loadDealerAndEmbedCode(id);
    }
  }, [id]);

  const loadDealerAndEmbedCode = async (dealerId: string) => {
    try {
      setIsLoading(true);
      
      const [dealerResponse, embedResponse] = await Promise.all([
        apiService.getDealer(dealerId),
        apiService.getEmbedCode(dealerId)
      ]);

      if (dealerResponse.success && dealerResponse.data) {
        setDealer(dealerResponse.data);
      } else {
        toast.error('Failed to load dealer information');
        navigate('/dealers');
        return;
      }

      if (embedResponse.success && embedResponse.data) {
        setEmbedCode(embedResponse.data);
      } else {
        toast.error('Failed to load embed code');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load embed code');
      navigate('/dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      toast.success('Copied to clipboard!');
      
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleRegenerateToken = async () => {
    if (!id || !dealer) return;
    
    if (!window.confirm(`Regenerate embed token for ${dealer.name}? This will invalidate the current token and require updating all embedded instances.`)) {
      return;
    }

    try {
      const response = await apiService.regenerateEmbedToken(id);
      if (response.success) {
        toast.success('Embed token regenerated successfully');
        loadDealerAndEmbedCode(id);
      } else {
        toast.error('Failed to regenerate token');
      }
    } catch (error) {
      console.error('Error regenerating token:', error);
      toast.error('Failed to regenerate token');
    }
  };

  const handlePreviewWebsite = () => {
    if (!dealer || !embedCode) return;

    // Generate the preview HTML content with the dealer's actual embed code
    const previewHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Preview: ${dealer.name} - Tire Scheduler</title>
    <style>
        body {
            font-family: ${dealer.config?.fontFamily || 'Arial, sans-serif'};
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            line-height: 1.6;
            background: #f8f9fa;
        }
        .header {
            background: white;
            padding: 30px;
            text-align: center;
            border-radius: 12px;
            margin-bottom: 30px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        .section {
            margin: 40px 0;
            padding: 30px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: white;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }
        .section h2 {
            color: #1f2937;
            margin-top: 0;
            border-bottom: 2px solid ${dealer.config?.primaryColor || '#ed2024'};
            padding-bottom: 10px;
        }
        .demo-info {
            background: #eff6ff;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
            border-left: 4px solid ${dealer.config?.primaryColor || '#ed2024'};
        }
        .contact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-top: 20px;
        }
        .contact-item {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            border-left: 3px solid ${dealer.config?.primaryColor || '#ed2024'};
        }
        .preview-badge {
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${dealer.config?.primaryColor || '#ed2024'};
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 0.875rem;
            font-weight: 600;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
    </style>
</head>
<body>
    <div class="preview-badge">
        📱 PREVIEW MODE
    </div>

    <div class="header">
        <h1>🚗 ${dealer.name}</h1>
        <p>Professional tire and automotive services</p>
        <div class="contact-grid">
            <div class="contact-item">
                <strong>📍 Address</strong><br>
                ${dealer.address}<br>
                ${dealer.city}, ${dealer.state} ${dealer.zipCode}
            </div>
            <div class="contact-item">
                <strong>📞 Phone</strong><br>
                ${dealer.phone}
            </div>
            <div class="contact-item">
                <strong>✉️ Email</strong><br>
                ${dealer.email}
            </div>
            ${dealer.website ? `
            <div class="contact-item">
                <strong>🌐 Website</strong><br>
                <a href="${dealer.website}" target="_blank" style="color: ${dealer.config?.primaryColor || '#ed2024'};">${dealer.website}</a>
            </div>
            ` : ''}
        </div>
    </div>

    <div class="section">
        <h2>Our Services</h2>
        <ul style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; list-style: none; padding: 0;">
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">🛞 Tire Installation & Rotation</li>
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">🛢️ Oil Changes & Maintenance</li>
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">🚗 Brake Service & Repair</li>
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">⚡ Engine Diagnostics</li>
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">🔋 Battery Replacement</li>
            <li style="padding: 10px; background: #f8f9fa; border-radius: 6px;">🔧 General Auto Repair</li>
        </ul>
    </div>

    <div class="section">
        <h2>Schedule Your Appointment</h2>
        <div class="demo-info">
            <strong>📝 Live Preview:</strong> This is how the tire scheduler will appear on your website. 
            The booking system is fully functional and connected to your account.
            <br><br>
            <strong>Configuration:</strong> 
            Token: <code style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-size: 0.875rem;">${dealer.embedToken}</code> | 
            Color: <span style="display: inline-block; width: 16px; height: 16px; background: ${dealer.config?.primaryColor || '#ed2024'}; border-radius: 50%; vertical-align: middle; margin: 0 4px;"></span>${dealer.config?.primaryColor || '#ed2024'} | 
            Font: ${dealer.config?.fontFamily || 'Arial, sans-serif'}
        </div>
        
        <!-- Tire Scheduler Widget -->
        <div id="tire-scheduler-widget"></div>
        <script>
          (function() {
            var script = document.createElement('script');
            script.src = 'https://scheduler.ndproductsandbox.com/embed.js';
            script.setAttribute('data-embed-token', '${dealer.embedToken}');
            ${dealer.config?.primaryColor ? `script.setAttribute('data-primary-color', '${dealer.config.primaryColor}');` : `script.setAttribute('data-primary-color', '#ed2024');`}
            ${dealer.config?.fontFamily ? `script.setAttribute('data-font-family', '${dealer.config.fontFamily}');` : `script.setAttribute('data-font-family', 'Arial, sans-serif');`}
            document.head.appendChild(script);
          })();
        </script>
    </div>

    <div class="section">
        <h2>Contact Information</h2>
        <p><strong>Business Hours:</strong><br>
        Monday - Friday: 8:00 AM - 6:00 PM<br>
        Saturday: 8:00 AM - 4:00 PM<br>
        Sunday: Closed</p>
        
        <p style="margin-top: 20px; padding: 15px; background: #f0f9ff; border-radius: 8px; border-left: 4px solid ${dealer.config?.primaryColor || '#ed2024'};">
            <strong>Ready to get started?</strong> Use the scheduler above to book your appointment online, 
            or give us a call at <strong>${dealer.phone}</strong> to speak with our team directly.
        </p>
    </div>

    <script>
        // Listen for scheduler events
        window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'appointment-booked') {
                console.log('Appointment booked:', event.data.appointment);
                // In preview mode, show a success message
                alert('✅ Appointment booked successfully! (Preview Mode)');
            }
        });
    </script>
</body>
</html>`;

    // Create a new window with the preview content
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
      previewWindow.document.title = `Preview: ${dealer.name} - Tire Scheduler`;
    } else {
      toast.error('Please allow popups to view the preview');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!dealer || !embedCode) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Failed to load embed code information.
        </div>
      </PageContainer>
    );
  }

  const tabs = [
    { id: 'full', label: 'Complete Example', code: embedCode.fullExample },
    { id: 'script', label: 'Script Tag', code: embedCode.script },
    { id: 'widget', label: 'Widget Container', code: embedCode.widget },
    { id: 'floating', label: 'Floating Button', code: embedCode.floatingButton }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/dealers')}>
          <ArrowLeft />
          Back to Dealers
        </BackButton>
        <h1>Embed Code</h1>
      </PageHeader>

      <DealerInfo>
        <div className="dealer-name">{dealer.name}</div>
        <div className="dealer-details">
          <span>{dealer.email}</span>
          <span>{dealer.city}, {dealer.state}</span>
          {dealer.website && (
            <a href={dealer.website} target="_blank" rel="noopener noreferrer" style={{ color: '#ed2024', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {dealer.website} <ExternalLink size={14} />
            </a>
          )}
        </div>
      </DealerInfo>

      <TokenInfo>
        <div className="token-header">
          <h3>Embed Token</h3>
        </div>
        <div className="token-value">{dealer.embedToken}</div>
        <div className="token-actions">
          <CopyButton
            copied={copiedStates['token']}
            onClick={() => copyToClipboard(dealer.embedToken || '', 'token')}
          >
            {copiedStates['token'] ? <Check /> : <Copy />}
            {copiedStates['token'] ? 'Copied!' : 'Copy Token'}
          </CopyButton>
          <ActionButton variant="danger" onClick={handleRegenerateToken}>
            <RotateCcw />
            Regenerate Token
          </ActionButton>
        </div>
      </TokenInfo>

      <CodeSection>
        <SectionHeader>
          <h2>
            <Code />
            Integration Code
          </h2>
        </SectionHeader>

        <TabContainer>
          <TabList>
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id as EmbedTab)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          {activeTab === 'full' && (
            <Description>
              Complete integration example with both embedding options. Copy and paste this into your HTML.
            </Description>
          )}
          {activeTab === 'script' && (
            <Description>
              The main script tag that loads the scheduler. This must be included in your page's &lt;head&gt; section.
            </Description>
          )}
          {activeTab === 'widget' && (
            <Description>
              Container for the embedded widget. Place this where you want the scheduler to appear on your page.
            </Description>
          )}
          {activeTab === 'floating' && (
            <Description>
              Floating button that opens the scheduler in a modal. Can be placed anywhere in your page body.
            </Description>
          )}

          <CodeBlock>
            <CodeHeader>
              <span className="code-title">
                {tabs.find(t => t.id === activeTab)?.label}
              </span>
              <CopyButton
                copied={copiedStates[activeTab]}
                onClick={() => copyToClipboard(tabs.find(t => t.id === activeTab)?.code || '', activeTab)}
              >
                {copiedStates[activeTab] ? <Check /> : <Copy />}
                {copiedStates[activeTab] ? 'Copied!' : 'Copy Code'}
              </CopyButton>
            </CodeHeader>
            <CodeContent>
              {tabs.find(t => t.id === activeTab)?.code}
            </CodeContent>
          </CodeBlock>
        </TabContainer>
      </CodeSection>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <ActionButton onClick={() => navigate(`/dealers/${id}/edit`)}>
          <Settings />
          Edit Dealer Settings
        </ActionButton>
        <ActionButton onClick={() => navigate(`/dealers/${id}/users`)}>
          <Users />
          Manage Users
        </ActionButton>
        <ActionButton variant="primary" onClick={handlePreviewWebsite}>
          <Eye />
          Preview on Website
        </ActionButton>
      </div>
    </PageContainer>
  );
};

export default EmbedCode;